// Admin-Panel › Nutzer-Detail: bearbeiten, sperren/entsperren, Passwort-Reset, überall abmelden, löschen.
import type { AdminUserDto } from '@lagerfeuer/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useAuthStore } from '../../../store/auth';
import { adminApi, type PasswordResetResult } from '../api';
import { absoluteUrl, displayName, errorMessage, formatBytes, formatDateTime } from '../format';
import { toast } from '../toast';
import { Button, ConfirmButton, ErrorBox, Field, Loading, SecretReveal } from '../ui';
import { deleteUserErrorText, PASSWORD_MIN, USERNAME_RE } from './hooks';
import { AdminError, AdminSection, UserBadges } from './parts';
import { ADMIN_STATS_KEY, ADMIN_USERS_KEY, adminUserKey } from './realtime';

type UpdateBody = Parameters<typeof adminApi.updateUser>[1];

function useUpdateUser(user: AdminUserDto) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateBody) => adminApi.updateUser(user.id, body),
    onSuccess: (u) => {
      qc.setQueryData(adminUserKey(u.id), u);
      void qc.invalidateQueries({ queryKey: ADMIN_USERS_KEY });
    },
  });
}

function ProfileForm(props: { user: AdminUserDto; isSelf: boolean }) {
  const u = props.user;
  const [username, setUsername] = useState(u.username);
  const [displayNameValue, setDisplayName] = useState(u.displayName ?? '');
  const [email, setEmail] = useState(u.email ?? '');
  const [isAdmin, setIsAdmin] = useState(u.isAdmin);
  const update = useUpdateUser(u);
  const body: UpdateBody = {};
  if (username !== u.username) body.username = username;
  if ((displayNameValue.trim() || null) !== u.displayName) body.displayName = displayNameValue.trim() || null;
  if ((email.trim() || null) !== u.email) body.email = email.trim() || null;
  if (isAdmin !== u.isAdmin) body.isAdmin = isAdmin;
  const dirty = Object.keys(body).length > 0;
  const usernameError = USERNAME_RE.test(username) ? null : '2–32 Zeichen: Kleinbuchstaben, Ziffern, _ und .';
  const selfAdminLock = props.isSelf && u.isAdmin;
  return (
    <AdminSection title="Profil" testId="admin-user-profile">
      <form
        className="lfx-stack"
        onSubmit={(e) => {
          e.preventDefault();
          if (!dirty || usernameError) return;
          update.mutate(body, { onSuccess: () => toast({ kind: 'success', title: 'Nutzer gespeichert' }) });
        }}
      >
        <Field label="Benutzername" error={usernameError}>
          {(id) => <input id={id} className="lfx-input" value={username} data-testid="admin-user-username" onChange={(e) => setUsername(e.target.value)} />}
        </Field>
        <Field label="Anzeigename">
          {(id) => (
            <input id={id} className="lfx-input" value={displayNameValue} data-testid="admin-user-displayname" onChange={(e) => setDisplayName(e.target.value)} />
          )}
        </Field>
        <Field label="E-Mail">
          {(id) => <input id={id} type="email" className="lfx-input" value={email} data-testid="admin-user-email" onChange={(e) => setEmail(e.target.value)} />}
        </Field>
        <label className="lfx-check">
          <input
            type="checkbox"
            checked={isAdmin}
            disabled={selfAdminLock}
            data-testid="admin-user-isadmin"
            onChange={(e) => setIsAdmin(e.target.checked)}
          />
          Plattform-Admin
        </label>
        {selfAdminLock && (
          <div className="lfx-field__hint" data-testid="admin-user-self-admin-hint">
            Du kannst dir die Admin-Rechte nicht selbst entziehen.
          </div>
        )}
        <ErrorBox error={update.error} testId="admin-user-save-error" />
        <div className="lfx-row">
          <Button type="submit" variant="primary" testId="admin-user-save" disabled={!dirty || !!usernameError || update.isPending}>
            Speichern
          </Button>
        </div>
      </form>
    </AdminSection>
  );
}

function DisableSection(props: { user: AdminUserDto; isSelf: boolean }) {
  const u = props.user;
  const [reason, setReason] = useState('');
  const update = useUpdateUser(u);
  return (
    <AdminSection title={u.disabled ? 'Konto gesperrt' : 'Konto sperren'} testId="admin-user-disable">
      {u.disabled ? (
        <div className="lfx-stack">
          <p className="lfx-muted" data-testid="admin-user-disabled-reason">
            Grund: {u.disabledReason || 'kein Grund angegeben'}
          </p>
          <div className="lfx-row">
            <Button
              testId="admin-user-enable"
              disabled={update.isPending}
              onClick={() =>
                update.mutate({ disabled: false, disabledReason: null }, { onSuccess: () => toast({ kind: 'success', title: 'Konto entsperrt' }) })
              }
            >
              Entsperren
            </Button>
          </div>
        </div>
      ) : (
        <div className="lfx-stack">
          <Field label="Grund (optional, für das Audit-Log)">
            {(id) => (
              <input
                id={id}
                className="lfx-input"
                value={reason}
                disabled={props.isSelf}
                data-testid="admin-user-disable-reason"
                onChange={(e) => setReason(e.target.value)}
              />
            )}
          </Field>
          <p className="lfx-muted">Sperren meldet den Nutzer sofort überall ab.</p>
          <div className="lfx-row">
            <ConfirmButton
              label="Sperren"
              confirmLabel="Wirklich sperren?"
              testId="admin-user-disable-submit"
              disabled={props.isSelf || update.isPending}
              onConfirm={() =>
                update.mutate(
                  { disabled: true, disabledReason: reason.trim() || null },
                  { onSuccess: () => toast({ kind: 'success', title: 'Konto gesperrt' }) },
                )
              }
            />
          </div>
          {props.isSelf && (
            <div className="lfx-field__hint" data-testid="admin-user-self-disable-hint">
              Du kannst dein eigenes Konto nicht sperren.
            </div>
          )}
        </div>
      )}
      <ErrorBox error={update.error} testId="admin-user-disable-error" />
    </AdminSection>
  );
}

function PasswordResetSection(props: { user: AdminUserDto }) {
  const [mode, setMode] = useState<'token' | 'password'>('token');
  const [newPassword, setNewPassword] = useState('');
  // Ergebnis nur im Komponentenzustand – nach „Ausblenden"/Verlassen nicht mehr abrufbar.
  const [result, setResult] = useState<PasswordResetResult | null>(null);
  const reset = useMutation({
    gcTime: 0, // Einmal-Geheimnis nicht im Mutation-Cache liegen lassen
    mutationFn: () => adminApi.resetPassword(props.user.id, mode === 'token' ? { mode: 'token' } : { mode: 'password', newPassword }),
    onSuccess: (r) => {
      setNewPassword('');
      if (mode === 'token') setResult(r ?? {});
      else toast({ kind: 'success', title: 'Neues Passwort gesetzt', body: 'Der Nutzer wurde überall abgemeldet.' });
    },
  });
  const pwError = mode === 'password' && newPassword.length > 0 && newPassword.length < PASSWORD_MIN ? `Mindestens ${PASSWORD_MIN} Zeichen.` : null;
  return (
    <AdminSection title="Passwort zurücksetzen" testId="admin-user-reset">
      <div className="lfx-stack">
        <fieldset className="lfx-admin-fieldset">
          <legend className="lfx-field__label">Verfahren</legend>
          <label className="lfx-check">
            <input type="radio" name="admin-reset-mode" checked={mode === 'token'} data-testid="admin-user-reset-mode-token" onChange={() => setMode('token')} />
            Reset-Link erzeugen (24 h gültig)
          </label>
          <label className="lfx-check">
            <input
              type="radio"
              name="admin-reset-mode"
              checked={mode === 'password'}
              data-testid="admin-user-reset-mode-password"
              onChange={() => setMode('password')}
            />
            Neues Passwort direkt setzen
          </label>
        </fieldset>
        {mode === 'password' && (
          <Field label="Neues Passwort" error={pwError}>
            {(id) => (
              <input
                id={id}
                type="password"
                autoComplete="new-password"
                className="lfx-input"
                value={newPassword}
                data-testid="admin-user-reset-password"
                onChange={(e) => setNewPassword(e.target.value)}
              />
            )}
          </Field>
        )}
        <div className="lfx-row">
          <Button
            testId="admin-user-reset-submit"
            disabled={reset.isPending || (mode === 'password' && newPassword.length < PASSWORD_MIN)}
            onClick={() => {
              setResult(null);
              reset.mutate();
            }}
          >
            {mode === 'token' ? 'Reset-Link erzeugen' : 'Passwort setzen'}
          </Button>
        </div>
        <ErrorBox error={reset.error} testId="admin-user-reset-error" />
        {result && (
          <div className="lfx-stack" data-testid="admin-user-reset-result">
            {result.resetUrl && <SecretReveal label="Reset-Link" value={absoluteUrl(result.resetUrl)} testId="admin-user-reset-url" />}
            {result.resetToken && <SecretReveal label="Reset-Token" value={result.resetToken} testId="admin-user-reset-token" />}
            {result.expiresAt && (
              <div className="lfx-muted" data-testid="admin-user-reset-expires">
                Gültig bis {formatDateTime(result.expiresAt)}
              </div>
            )}
            <div className="lfx-row">
              <Button testId="admin-user-reset-hide" onClick={() => setResult(null)}>
                Ausblenden
              </Button>
            </div>
          </div>
        )}
      </div>
    </AdminSection>
  );
}

function DangerSection(props: { user: AdminUserDto; isSelf: boolean }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const logout = useMutation({
    mutationFn: () => adminApi.logoutUser(props.user.id),
    onSuccess: () => toast({ kind: 'success', title: 'Überall abgemeldet' }),
  });
  const remove = useMutation({
    mutationFn: () => adminApi.deleteUser(props.user.id),
    onSuccess: () => {
      qc.removeQueries({ queryKey: adminUserKey(props.user.id) });
      void qc.invalidateQueries({ queryKey: ADMIN_USERS_KEY });
      void qc.invalidateQueries({ queryKey: ADMIN_STATS_KEY });
      toast({ kind: 'success', title: 'Nutzer gelöscht' });
      void navigate('..', { relative: 'path' });
    },
  });
  const deleteError = remove.error ? (deleteUserErrorText(remove.error) ?? errorMessage(remove.error)) : null;
  return (
    <AdminSection title="Sitzungen & Löschen" testId="admin-user-danger">
      <div className="lfx-stack">
        <div className="lfx-row">
          <Button testId="admin-user-logout" disabled={logout.isPending} onClick={() => logout.mutate()}>
            Überall abmelden
          </Button>
          <ConfirmButton
            label="Nutzer löschen"
            confirmLabel="Endgültig löschen?"
            variant="danger"
            testId="admin-user-delete"
            disabled={props.isSelf || remove.isPending}
            onConfirm={() => remove.mutate()}
          />
        </div>
        {props.isSelf && (
          <div className="lfx-field__hint" data-testid="admin-user-self-delete-hint">
            Dein eigenes Konto kannst du hier nicht löschen.
          </div>
        )}
        <ErrorBox error={logout.error} testId="admin-user-logout-error" />
        {deleteError && (
          <div className="lfx-error" role="alert" data-testid="admin-user-delete-error">
            {deleteError}
          </div>
        )}
      </div>
    </AdminSection>
  );
}

export function UserDetailPage() {
  const { userId = '' } = useParams();
  const me = useAuthStore((s) => s.user);
  const query = useQuery({ queryKey: adminUserKey(userId), queryFn: () => adminApi.user(userId), enabled: !!userId });
  const u = query.data;
  const isSelf = !!u && u.id === me?.id;
  return (
    <div className="lfx-stack" data-testid="admin-user-detail">
      <Link to=".." relative="path" className="lfx-link" data-testid="admin-user-back">
        ← Alle Nutzer
      </Link>
      {query.isLoading && <Loading testId="admin-user-loading" />}
      <AdminError error={query.error} testId="admin-user-error" />
      {u && (
        <>
          <div className="lfx-card lfx-stack">
            <div className="lfx-row">
              <h2 className="lfx-admin-section__title" data-testid="admin-user-title">
                {displayName(u)}
              </h2>
              <span className="lfx-muted">@{u.username}</span>
              <UserBadges user={u} />
            </div>
            <div className="lfx-muted">
              ID <span className="lfx-mono">{u.id}</span> · erstellt {formatDateTime(u.createdAt)} · zuletzt aktiv {formatDateTime(u.lastSeenAt)} ·{' '}
              {u.serverCount} Server · {formatBytes(u.storageBytes)} Speicher
              {u.discord ? ` · Discord: ${u.discord.username}` : ''}
            </div>
          </div>
          {/* key: Formular nach dem Speichern mit den Serverwerten neu aufbauen */}
          <ProfileForm key={`${u.username}|${u.displayName}|${u.email}|${u.isAdmin}`} user={u} isSelf={isSelf} />
          <DisableSection user={u} isSelf={isSelf} />
          <PasswordResetSection user={u} />
          <DangerSection user={u} isSelf={isSelf} />
        </>
      )}
    </div>
  );
}
